// Task-list grouping helpers.
//
// The admin Tasks view used to render a flat list; selecting a node in the
// left tree would collapse every descendant task into a single scroll list
// with no visible grouping. This module groups that flat list by one axis
// (floor, unit, milestone, …) so planners can see shape at a glance.
//
// All helpers here are pure and data-only — no React. They're unit-testable
// in isolation and re-usable by the Planning view if we decide to ship the
// same behaviour there later.

import type { AdminTask, TaskStatus } from '../../lib/api';
import type { TreeNodeFilters } from '../shared/ProjectTreePanel';

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

export type GroupBy =
    | 'auto'          // resolve from tree selection — see computeAutoGroupBy
    | 'none'          // flat list (the old behaviour)
    | 'milestone'
    | 'building'
    | 'category'
    | 'subCategory'
    | 'floor'
    | 'unit'
    | 'status'
    | 'engineer';

export interface TaskGroup {
    key: string;                     // stable id, safe to use as React key
    label: string;                   // rendered in the header
    axis: Exclude<GroupBy, 'auto' | 'none'>;
    tasks: AdminTask[];
    // Aggregate status counts for the header chips.
    counts: {
        total: number;
        inProgress: number;
        onHold: number;
        notStarted: number;
        completed: number;
        openIssues: number;          // sum of task.openIssuesCount
    };
}

// Labels for the "Group by" selector dropdown.
export const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
    { value: 'auto',        label: 'Auto (match tree)' },
    { value: 'none',        label: 'No grouping' },
    { value: 'building',    label: 'Building' },
    { value: 'category',    label: 'Category' },
    { value: 'subCategory', label: 'Subcategory' },
    { value: 'floor',       label: 'Floor' },
    { value: 'unit',        label: 'Unit' },
    { value: 'milestone',   label: 'Milestone' },
    { value: 'status',      label: 'Status' },
    { value: 'engineer',    label: 'Engineer' },
];

// ────────────────────────────────────────────────────────────────────────────
// Auto-axis
// ────────────────────────────────────────────────────────────────────────────

/**
 * Pick a sensible default grouping axis based on the tree node the user has
 * selected. The rule: group by the **next level down** in the
 * building → category → subcategory → floor → unit chain.
 *
 * Leaves (a Unit selected) return 'none' — we can't go any deeper.
 * Root (nothing selected) returns 'building'.
 */
export function computeAutoGroupBy(
    filters: TreeNodeFilters,
): Exclude<GroupBy, 'auto'> {
    if (filters.unitId)        return 'none';
    if (filters.floorId)       return 'unit';
    if (filters.subcategoryId) return 'floor';
    if (filters.categoryId)    return 'subCategory';
    if (filters.buildingId)    return 'category';
    return 'building';
}

/**
 * Resolve 'auto' down to a concrete axis so the rest of the code can be
 * simple. Never returns 'auto'.
 */
export function resolveGroupBy(
    mode: GroupBy,
    filters: TreeNodeFilters,
): Exclude<GroupBy, 'auto'> {
    return mode === 'auto' ? computeAutoGroupBy(filters) : mode;
}

// ────────────────────────────────────────────────────────────────────────────
// Grouping
// ────────────────────────────────────────────────────────────────────────────

const UNASSIGNED_KEY = '__unassigned__';

/** Pull the (key, label) pair for a single task on a given axis. */
function keyLabelFor(
    task: AdminTask,
    axis: Exclude<GroupBy, 'auto' | 'none'>,
): { key: string; label: string } {
    switch (axis) {
        case 'milestone':
            return task.milestoneTaskId
                ? { key: task.milestoneTaskId, label: task.milestoneName ?? 'Milestone' }
                : { key: UNASSIGNED_KEY, label: 'No milestone' };
        case 'building':
            return task.building
                ? { key: `b:${task.building}`, label: task.building }
                : { key: UNASSIGNED_KEY, label: 'No building' };
        case 'category':
            return task.category
                ? { key: `c:${task.category}`, label: task.category }
                : { key: UNASSIGNED_KEY, label: 'No category' };
        case 'subCategory':
            return task.subCategory
                ? { key: `s:${task.subCategory}`, label: task.subCategory }
                : { key: UNASSIGNED_KEY, label: 'No subcategory' };
        case 'floor':
            return task.floor
                ? { key: `f:${task.floor}`, label: task.floor }
                : { key: UNASSIGNED_KEY, label: 'No floor' };
        case 'unit':
            return task.unit
                ? { key: `u:${task.unit}`, label: task.unit }
                : { key: UNASSIGNED_KEY, label: 'No unit' };
        case 'status':
            return { key: `st:${task.status}`, label: task.status };
        case 'engineer':
            return task.assignedEngineerName
                ? {
                      key: `e:${task.assignedEngineerId ?? task.assignedEngineerName}`,
                      label: task.assignedEngineerName,
                  }
                : { key: UNASSIGNED_KEY, label: 'Unassigned' };
    }
}

/**
 * Group a task list by the supplied axis.
 *
 * - Groups are returned in alphabetical label order.
 * - The "Unassigned" bucket (tasks missing the axis key) always sorts last.
 * - Status groupings follow a workflow order instead of alphabetical, so the
 *   sort reads left-to-right like the mobile card actions.
 */
export function groupTasks(
    tasks: AdminTask[],
    axis: Exclude<GroupBy, 'auto' | 'none'>,
): TaskGroup[] {
    if (tasks.length === 0) return [];

    const map = new Map<string, TaskGroup>();
    for (const task of tasks) {
        const { key, label } = keyLabelFor(task, axis);
        let group = map.get(key);
        if (!group) {
            group = {
                key,
                label,
                axis,
                tasks: [],
                counts: {
                    total: 0, inProgress: 0, onHold: 0,
                    notStarted: 0, completed: 0, openIssues: 0,
                },
            };
            map.set(key, group);
        }
        group.tasks.push(task);
        group.counts.total += 1;
        group.counts.openIssues += task.openIssuesCount ?? 0;
        switch (task.status as TaskStatus) {
            case 'In Progress': group.counts.inProgress += 1; break;
            case 'On Hold':     group.counts.onHold     += 1; break;
            case 'Not Started': group.counts.notStarted += 1; break;
            case 'Completed':   group.counts.completed  += 1; break;
        }
    }

    const groups = Array.from(map.values());

    // Sort by label, but pin "Unassigned" to the bottom.
    const statusOrder: Record<string, number> = {
        'st:In Progress': 0,
        'st:On Hold':     1,
        'st:Not Started': 2,
        'st:Completed':   3,
    };
    groups.sort((a, b) => {
        if (a.key === UNASSIGNED_KEY) return 1;
        if (b.key === UNASSIGNED_KEY) return -1;
        if (axis === 'status') {
            return (statusOrder[a.key] ?? 99) - (statusOrder[b.key] ?? 99);
        }
        return a.label.localeCompare(b.label, undefined, { numeric: true });
    });

    return groups;
}

// ────────────────────────────────────────────────────────────────────────────
// Collapse defaults
// ────────────────────────────────────────────────────────────────────────────

// Collapse any single group that has more than this many rows, so a crowded
// floor doesn't wall off the other floors.
const LARGE_GROUP_COLLAPSE_THRESHOLD = 30;
// If the *total* rendered task count exceeds this, start every group collapsed.
const TOTAL_COLLAPSE_ALL_THRESHOLD = 300;

/**
 * Decide which groups should start collapsed based on size. Pure — callers
 * seed their `collapsedGroupKeys` state from this on first render / reshape.
 */
export function initialCollapsedKeys(groups: TaskGroup[]): Set<string> {
    const total = groups.reduce((sum, g) => sum + g.counts.total, 0);
    if (total > TOTAL_COLLAPSE_ALL_THRESHOLD) {
        return new Set(groups.map((g) => g.key));
    }
    const collapsed = new Set<string>();
    for (const g of groups) {
        if (g.counts.total > LARGE_GROUP_COLLAPSE_THRESHOLD) {
            collapsed.add(g.key);
        }
    }
    return collapsed;
}
